import { useCallback, useEffect, useState } from 'react';
import { supabase } from './supabase';

// ─────────────────────────────────────────────────────────────
// Support tickets (Tickets) — sidebar open-count badge.
//
// "Open" is status != 'solved', matching the "Abiertos" chip that
// SuperAdminTickets defaults to, so the badge and the list agree.
//
// ponytail: head + exact count, so no rows cross the wire. Gated on
// `enabled` because the nav item is super-admin-only — everyone else
// would be paying for a query whose badge never renders.
// ─────────────────────────────────────────────────────────────

export function useOpenTicketCount(enabled: boolean) {
    const [openCount, setOpenCount] = useState(0);

    const refresh = useCallback(async () => {
        if (!supabase || !enabled) return;
        const { count, error } = await supabase
            .from('support_tickets')
            .select('id', { count: 'exact', head: true })
            .eq('is_deleted', false)
            .neq('status', 'solved');
        if (!error) setOpenCount(count ?? 0);
    }, [enabled]);

    useEffect(() => { refresh(); }, [refresh]);

    // Realtime: any ticket change can move the count — a new ticket bumps it,
    // resolving one drops it. Unique channel name per mount, same reason as
    // useOmnichannelUnread: supabase-js reuses the instance for a given name,
    // which breaks across Fast Refresh / StrictMode remounts.
    useEffect(() => {
        if (!supabase || !enabled) return;
        const channel = supabase
            .channel(`open_tickets_${Math.random().toString(36).slice(2)}`)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'support_tickets' }, () => { refresh(); })
            .subscribe();
        return () => { supabase.removeChannel(channel); };
    }, [enabled, refresh]);

    return { openCount, refresh };
}

// ─────────────────────────────────────────────────────────────
// Cumplimiento — sidebar pending-count badge (super admin only).
//
// Pending work on the Cumplimiento screen is two queues: infraction
// reports awaiting review (property_infractions.status = 'pending',
// the "Pendiente" chip SuperAdminInfracciones defaults to) and
// unresolved appeals (share_compliance_appeals.status = 'pending').
// The badge is their sum so neither queue can go stale invisibly; the
// per-queue counts label the Reportes / Apelaciones tabs on that screen.
// ─────────────────────────────────────────────────────────────

export function useCompliancePendingCount(enabled: boolean) {
    const [pending, setPending] = useState({ infractions: 0, appeals: 0 });

    const refresh = useCallback(async () => {
        if (!supabase || !enabled) return;
        const [infractions, appeals] = await Promise.all([
            supabase.from('property_infractions').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
            supabase.from('share_compliance_appeals').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
        ]);
        if (!infractions.error && !appeals.error) {
            setPending({ infractions: infractions.count ?? 0, appeals: appeals.count ?? 0 });
        }
    }, [enabled]);

    useEffect(() => { refresh(); }, [refresh]);

    // Realtime: same unique-channel-per-mount reasoning as useOpenTicketCount.
    useEffect(() => {
        if (!supabase || !enabled) return;
        const channel = supabase
            .channel(`compliance_pending_${Math.random().toString(36).slice(2)}`)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'property_infractions' }, () => { refresh(); })
            .on('postgres_changes', { event: '*', schema: 'public', table: 'share_compliance_appeals' }, () => { refresh(); })
            .subscribe();
        return () => { supabase.removeChannel(channel); };
    }, [enabled, refresh]);

    return {
        pendingCount: pending.infractions + pending.appeals,
        infractionsPending: pending.infractions,
        appealsPending: pending.appeals,
        refresh,
    };
}

// ─────────────────────────────────────────────────────────────
// Sugerencias — sidebar new-count badge (super admin only).
//
// Counts feature_requests still in status='new': a suggestion stays on
// the badge until the super admin moves it off 'new' (planned /
// in_progress / done / declined) from the Sugerencias board.
// ─────────────────────────────────────────────────────────────

export function useSugerenciasNewCount(enabled: boolean) {
    const [newCount, setNewCount] = useState(0);

    const refresh = useCallback(async () => {
        if (!supabase || !enabled) return;
        const { count, error } = await supabase
            .from('feature_requests')
            .select('id', { count: 'exact', head: true })
            .eq('status', 'new')
            .eq('is_deleted', false);
        if (!error) setNewCount(count ?? 0);
    }, [enabled]);

    useEffect(() => { refresh(); }, [refresh]);

    useEffect(() => {
        if (!supabase || !enabled) return;
        const channel = supabase
            .channel(`sugerencias_new_${Math.random().toString(36).slice(2)}`)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'feature_requests' }, () => { refresh(); })
            .subscribe();
        return () => { supabase.removeChannel(channel); };
    }, [enabled, refresh]);

    return { newCount, refresh };
}

// ─────────────────────────────────────────────────────────────
// Blog Inmobiliario — Contenido y Portales pending-review badge.
//
// Counts blog_posts in status='pending': agency articles waiting for a
// super admin to approve or reject them — the same set as the
// "En revisión" chip on SuperAdminBlog. Live updates need blog_posts in
// the supabase_realtime publication (20260927233000_blog_review_alerts).
// ─────────────────────────────────────────────────────────────

export function useBlogPendingCount(enabled: boolean) {
    const [pendingCount, setPendingCount] = useState(0);

    const refresh = useCallback(async () => {
        if (!supabase || !enabled) return;
        const { count, error } = await supabase
            .from('blog_posts')
            .select('id', { count: 'exact', head: true })
            .eq('status', 'pending');
        if (!error) setPendingCount(count ?? 0);
    }, [enabled]);

    useEffect(() => { refresh(); }, [refresh]);

    useEffect(() => {
        if (!supabase || !enabled) return;
        const channel = supabase
            .channel(`blog_pending_${Math.random().toString(36).slice(2)}`)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'blog_posts' }, () => { refresh(); })
            .subscribe();
        return () => { supabase.removeChannel(channel); };
    }, [enabled, refresh]);

    return { pendingCount, refresh };
}
